import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./UploadedDataSystemMappingModal.module.css";

/**
 * UploadedDataSystemMappingModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - csvHeaders: string[]
 * - sampleRows: string[][]   // a few rows; we only use row[0] for example values
 * - transactionFormat: { version: string, fields: Array<{ path: string, type: string, required?: boolean }> }
 * - onConfirm: ({ mappings, defaults }) => void
 *
 * Result shape:
 * - mappings: Array<{ sourceHeader: string, targetPath: string | "IGNORE" }>
 * - defaults: { autoGenerateId: boolean, defaultCurrencyCode: string, defaultTypeName: "Expense"|"Income"|"Transfer" }
 */

const FRIENDLY_LABELS = {
  id: "ID",
  date: "Date",
  description: "Description",
  amount: "Amount",
  currencyCode: "Currency",
  typeName: "Type",
  tagPath: "Tags",
  accountName: "Account (Name)",
  status: "Status",
  memo: "Memo",
  url: "URL",
};

const TARGET_WHITELIST = new Set([
  "id",
  "date",
  "description",
  "amount",
  "currencyCode",
  "typeName",
  "tagPath",
  "accountName",
  "status",
  "memo",
  "url",
]);

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function suggestTargetForHeader(h) {
  const n = normalizeHeader(h);

  // exacts
  if (["date", "transaction date", "posted date"].includes(n)) return "date";
  if (["description", "memo", "merchant", "narrative"].includes(n)) return "description";
  if (["amount", "amt", "value"].includes(n)) return "amount";
  if (["currency", "currency code", "ccy"].includes(n)) return "currencyCode";
  if (["type", "txn type", "transaction type"].includes(n)) return "typeName";
  if (["tags", "labels", "categories"].includes(n)) return "tagPath";
  if (["account", "account name"].includes(n)) return "accountName";
  if (["status", "state"].includes(n)) return "status";
  if (["memo", "note", "notes"].includes(n)) return "memo";
  if (["url", "link"].includes(n)) return "url";
  if (["id", "txn id", "transaction id"].includes(n)) return "id";

  // fallback: try to match a single token to common targets
  if (/\bdate\b/.test(n)) return "date";
  if (/\bamount\b/.test(n)) return "amount";
  if (/\bcurr/.test(n)) return "currencyCode";
  if (/\btag|category|label/.test(n)) return "tagPath";
  if (/\baccount/.test(n)) return "accountName";
  if (/\btype/.test(n)) return "typeName";
  if (/\bmemo|note/.test(n)) return "memo";
  if (/\bstatus|state/.test(n)) return "status";

  return "IGNORE";
}

function friendlyName(path) {
  return FRIENDLY_LABELS[path] || path;
}

/** Fields from the system format we actually expose for mapping */
function useSystemTargets(transactionFormat) {
  return useMemo(() => {
    const all = Array.isArray(transactionFormat?.fields) ? transactionFormat.fields : [];
    // project to {path, type, required}
    const projected = all
      .filter((f) => TARGET_WHITELIST.has(f.path))
      .map((f) => ({ path: f.path, type: f.type, required: !!f.required }));
    // Sort by a sensible order
    const order = [
      "date",
      "description",
      "amount",
      "currencyCode",
      "typeName",
      "tagPath",
      "accountName",
      "status",
      "memo",
      "url",
      "id",
    ];
    projected.sort((a, b) => order.indexOf(a.path) - order.indexOf(b.path));
    return projected;
  }, [transactionFormat]);
}

/** Required fields that should be satisfied by mapping OR defaults (not hard-blocked by userEmail/accountId here) */
function requiredFromCsvOrDefaults(transactionFormat) {
  const requiredSet = new Set();
  for (const f of transactionFormat?.fields || []) {
    if (!f.required) continue;
    // Skip fields we don't expect to come from CSV in this step
    if (["userEmail", "accountId", "createdAt", "updatedAt"].includes(f.path)) continue;
    // We will allow 'id' to be auto-generated
    if (f.path === "id") continue;
    requiredSet.add(f.path);
  }
  // Ensure minimally: date & amount (in case format doc evolves)
  requiredSet.add("date");
  requiredSet.add("amount");
  return requiredSet;
}

const DEFAULTS_INIT = {
  autoGenerateId: true,
  defaultCurrencyCode: "CAD",
  defaultTypeName: "Expense",
};

export default function UploadedDataSystemMappingModal({
  isOpen,
  onClose,
  csvHeaders,
  sampleRows,
  transactionFormat,
  onConfirm,
}) {
  const systemTargets = useSystemTargets(transactionFormat);
  const [defaults, setDefaults] = useState(DEFAULTS_INIT);

  // Initialize mapping with suggestions (exact/heuristic)
  const initialMap = useMemo(() => {
    const used = new Set();
    const map = {};
    for (const h of csvHeaders || []) {
      const suggestion = suggestTargetForHeader(h);
      // avoid duplicate claims (e.g., two "Amount" columns)
      if (suggestion !== "IGNORE" && used.has(suggestion)) {
        map[h] = "IGNORE";
      } else {
        map[h] = suggestion;
        used.add(suggestion);
      }
    }
    return map;
  }, [csvHeaders]);

  const [mapping, setMapping] = useState(initialMap);
  useEffect(() => setMapping(initialMap), [initialMap]);

  const requiredSet = useMemo(
    () => requiredFromCsvOrDefaults(transactionFormat),
    [transactionFormat]
  );

  const exampleRow = sampleRows?.find((r) => Array.isArray(r)) || [];
  const mappedTargets = useMemo(() => new Set(Object.values(mapping)), [mapping]);

  const missingRequired = useMemo(() => {
    const missing = new Set();
    for (const req of requiredSet) {
      // Satisfied if a CSV header maps to it OR a default covers it
      let covered = [...mappedTargets].includes(req);
      if (!covered) {
        if (req === "currencyCode" && defaults.defaultCurrencyCode) covered = true;
        if (req === "typeName" && defaults.defaultTypeName) covered = true;
      }
      if (!covered) missing.add(req);
    }
    return missing;
  }, [requiredSet, mappedTargets, defaults]);

  if (!isOpen) return null;

  function setHeaderTarget(header, target) {
    setMapping((prev) => ({ ...prev, [header]: target }));
  }

  function handleConfirm() {
    // Normalize mapping into array; filter out IGNORE
    const mappings = (csvHeaders || []).map((h) => ({
      sourceHeader: h,
      targetPath: mapping[h] || "IGNORE",
    }));
    onConfirm?.({
      mappings,
      defaults,
    });
  }

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Map columns to system fields</h2>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Left: CSV columns */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Uploaded Columns</div>
            <div className={styles.tableWrap}>
              <table className={styles.mapTable}>
                <thead>
                  <tr>
                    <th>Uploaded Header</th>
                    <th>Example</th>
                    <th>Map To</th>
                  </tr>
                </thead>
                <tbody>
                  {(csvHeaders || []).map((h, i) => {
                    const current = mapping[h] || "IGNORE";
                    const ex = exampleRow[i] ?? "";
                    const isIgnored = current === "IGNORE";
                    return (
                      <tr
                        key={h || i}
                        className={isIgnored ? styles.rowIgnored : undefined}
                        data-ignored={isIgnored ? "1" : undefined}
                      >
                        <td className={styles.headerCell}>
                          <div className={styles.headerChip}>{h || <em>(unnamed)</em>}</div>
                        </td>
                        <td className={styles.exampleCell}>
                          <div className={styles.exampleText} title={String(ex)}>
                            {String(ex || "").slice(0, 64)}
                          </div>
                        </td>
                        <td
                          className={`${styles.selectCell} ${isIgnored ? styles.isIgnored : ""}`}
                          data-ignored={isIgnored ? "1" : undefined}
                          title={isIgnored ? "This column will be ignored" : undefined}
                        >
                          <select
                            className={styles.select}
                            value={current}
                            onChange={(e) => setHeaderTarget(h, e.target.value)}
                          >
                            <option value="IGNORE">Ignore</option>
                            {systemTargets.map((t) => (
                              <option key={t.path} value={t.path}>
                                {friendlyName(t.path)}
                                {t.required && requiredSet.has(t.path) ? " *" : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.legend}>
              <span className={styles.reqStar}>*</span> required (or must be satisfied via defaults)
            </div>
          </div>

          {/* Right: Defaults & status */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Defaults & Settings</div>

            <div className={styles.formRow}>
              <label className={styles.label}>
                <input
                  type="checkbox"
                  checked={defaults.autoGenerateId}
                  onChange={(e) =>
                    setDefaults((d) => ({ ...d, autoGenerateId: e.target.checked }))
                  }
                />{" "}
                Auto-generate missing <code>ID</code>
              </label>
              <div className={styles.hint}>Use a stable hash when ID column isn’t mapped.</div>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="currencyDefault">
                Default Currency
              </label>
              <input
                id="currencyDefault"
                className={styles.input}
                value={defaults.defaultCurrencyCode}
                onChange={(e) =>
                  setDefaults((d) => ({ ...d, defaultCurrencyCode: e.target.value.toUpperCase().slice(0, 3) }))
                }
                placeholder="e.g., CAD"
                maxLength={3}
              />
              <div className={styles.hint}>Used when no column is mapped to Currency.</div>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="typeDefault">
                Default Type
              </label>
              <select
                id="typeDefault"
                className={styles.select}
                value={defaults.defaultTypeName}
                onChange={(e) =>
                  setDefaults((d) => ({ ...d, defaultTypeName: e.target.value }))
                }
              >
                <option value="Expense">Expense</option>
                <option value="Income">Income</option>
                <option value="Transfer">Transfer</option>
              </select>
              <div className={styles.hint}>Used when no column is mapped to Type.</div>
            </div>

            <div className={styles.separator} />

            <div className={styles.statusBox}>
              <div className={styles.statusTitle}>Mapping Status</div>
              {missingRequired.size === 0 ? (
                <div className={styles.statusOk}>All required fields are satisfied.</div>
              ) : (
                <>
                  <div className={styles.statusWarn}>Missing required:</div>
                  <ul className={styles.missList}>
                    {[...missingRequired].map((p) => (
                      <li key={p}>
                        <code>{friendlyName(p)}</code>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={`btn btn-outline-secondary ${styles.footerBtn}`} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={missingRequired.size > 0}
            onClick={handleConfirm}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

UploadedDataSystemMappingModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  csvHeaders: PropTypes.arrayOf(PropTypes.string),
  sampleRows: PropTypes.arrayOf(PropTypes.array),
  transactionFormat: PropTypes.shape({
    version: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        path: PropTypes.string.isRequired,
        type: PropTypes.string,
        required: PropTypes.bool,
      })
    ),
  }),
  onConfirm: PropTypes.func,
};

UploadedDataSystemMappingModal.defaultProps = {
  isOpen: false,
  onClose: () => {},
  csvHeaders: [],
  sampleRows: [],
  transactionFormat: { version: "v1", fields: [] },
  onConfirm: () => {},
};
